import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import useSheetViewport, { isIOS } from "./useSheetViewport";
import "./sheet.css";

/**
 * Sheet genérico (modo claro, tema por variables --sh-*): panel lateral en escritorio
 * (`side="right"`) o bottom sheet en móvil (`side="bottom"`). Misma mecánica que el del
 * superadmin: drag para cerrar desde el header (Pointer Events + capture), cierre por
 * velocidad (> 0.11 px/ms) o distancia (30 % del alto), rubber-band hacia arriba.
 * Transiciones solo en transform/opacity, < 300 ms; con prefers-reduced-motion, crossfade.
 *
 * `title` puede omitirse (p. ej. menú "Más"): entonces el header solo lleva el handle.
 */
export default function Sheet({ open, onClose, side = "bottom", title, subtitle, header, footer, children, className = "", autoFocus = true }) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), 260);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, onClose]);

  // Bloqueo de scroll, sheet pegado al área visible con teclado y campo a la vista.
  useSheetViewport({ mounted, rootRef, panelRef, bodySelector: ".sh-body" });

  // Autofocus del primer campo, en escritorio y Android: se espera a que termine la
  // animación de entrada (260ms) para no pelear con ella. En iOS no: Safari no abre el
  // teclado con un focus() que no venga directo de un toque, así que el campo quedaba
  // enfocado sin teclado; al tocarlo después ya no había `focusin` y Safari desplazaba
  // la página (la trabada al enfocar).
  useEffect(() => {
    // `autoFocus={false}`: el sheet decide él mismo cuándo enfocar (p. ej. Nueva cita
    // enfoca el nombre hasta que se elige la hora).
    if (!shown || isIOS || !autoFocus) return;
    const panel = panelRef.current;
    if (!panel) return;
    const t = setTimeout(() => {
      panel.querySelector('.sh-body input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([disabled]), .sh-body textarea, .sh-body select')
        ?.focus({ preventScroll: true });
    }, 260);
    return () => clearTimeout(t);
  }, [shown, autoFocus]);

  const onPointerDown = useCallback((e) => {
    if (side !== "bottom" || (e.pointerType === "mouse" && e.button !== 0)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startY: e.clientY, lastY: e.clientY, lastT: performance.now(), dy: 0, v: 0 };
    setDragging(true);
  }, [side]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d || !panelRef.current) return;
    const now = performance.now();
    const raw = e.clientY - d.startY;
    const dy = raw >= 0 ? raw : -(Math.abs(raw) * 0.35) / (1 + Math.abs(raw) / 200);
    const dt = Math.max(1, now - d.lastT);
    d.v = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY; d.lastT = now; d.dy = raw;
    panelRef.current.style.transform = `translate3d(0, ${dy}px, 0)`;
  }, []);

  const onPointerUp = useCallback(() => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d || !panelRef.current) return;
    const height = panelRef.current.offsetHeight || 1;
    const shouldClose = d.v > 0.11 || d.dy > height * 0.3;
    panelRef.current.style.transform = "";
    if (shouldClose) onClose?.();
  }, [onClose]);

  if (!mounted) return null;

  return createPortal(
    <div ref={rootRef} className={`sh-root sh-${side} ${className}`} data-state={shown ? "open" : "closed"}>
      <div className="sh-scrim" onClick={onClose} />
      <section ref={panelRef} className={`sh-panel ${dragging ? "is-dragging" : ""}`} role="dialog" aria-modal="true" aria-label={title || "Menú"}>
        <header
          className={`sh-head ${title ? "" : "is-bare"}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {side === "bottom" && <span className="sh-handle" aria-hidden />}
          {(title || side === "right") && (
            <div className="sh-titlebar">
              <div className="sh-titles">
                {title && <h2 className="sh-title">{title}</h2>}
                {subtitle && <div className="sh-subtitle">{subtitle}</div>}
              </div>
              <button type="button" className="sh-x" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
            </div>
          )}
          {header}
        </header>
        <div className="sh-body">{children}</div>
        {footer && <footer className="sh-foot">{footer}</footer>}
      </section>
    </div>,
    document.body,
  );
}
